import { makeStyles, Typography } from "@material-ui/core"
import { FC, useState } from "react"
import { ClientFragment } from "../../generated/graphql"
import { colors } from "../../utils/theme"
import { QuestionnairesChartData } from "../../utils/types"
import Button from "../atoms/button/Button"
import DisabledActionTooltip from "../molecules/DisabledActionTooltip"
import QuestionnairesChartHelpIcon from "../molecules/QuestionnairesHelpIcon"
import QuestionnaireChartsContainer from "../organisms/questionnaires/displayChart"
import { ScheduleDialog } from "./ScheduleDialog"

const SCHEDULING_DISABLED_TOOLTIP =
  "This action is disabled because the client is disconnected."

const useStyles = makeStyles((theme) => ({
  noQuestionnaires: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: 202,
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
  },
  content: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  text: {
    fontWeight: 400,
    textAlign: "center",
    maxWidth: 502,
    marginBottom: theme.spacing(2),
    color: colors.neutral.asphalt,
  },
  button: {
    width: "fit-content",
    "& .MuiButton-label": {
      padding: theme.spacing(0, 5),
      lineHeight: "normal",
    },
  },
  titleHelp: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    color: colors.neutral.denim,
    fontWeight: 700,
    marginRight: 6,
  },
}))

const QuestionnairesOverview: FC<{
  client: ClientFragment
  canSchedule: boolean
}> = ({ client, canSchedule }) => {
  const classes = useStyles()
  const [openScheduleDialog, setOpenScheduleDialog] = useState<boolean>(false)
  const [chartLength, setChartLength] = useState<number>(0)

  const chartData = (data: QuestionnairesChartData) => {
    setChartLength(data.chartLength)
  }

  return (
    <>
      <div className={classes.titleHelp}>
        <Typography variant="h3" className={classes.title}>
          Assessments
        </Typography>
        <QuestionnairesChartHelpIcon />
      </div>
      {/* If we want to make this collapsible, all we gotta do is uncomment this and add an import */}
      {/* <CollapsibleAccordion="Charts"> */}
      <QuestionnaireChartsContainer
        client={client}
        chartCallbackData={chartData}
      />
      {/* </CollapsibleAccordion> */}
      {chartLength === 0 && (
        <div className={classes.noQuestionnaires}>
          <div className={classes.content}>
            <Typography variant="body1" className={classes.text}>
              {`It looks like ${client.first_name} ${client.last_name} hasn’t completed an assessment yet. ` +
                "Once they complete one, you’ll get to see their scores."}
            </Typography>
            <DisabledActionTooltip
              disabled={!canSchedule}
              text={SCHEDULING_DISABLED_TOOLTIP}
            >
              <Button
                customVariant="secondary"
                className={classes.button}
                disabled={!canSchedule}
                onClick={() => setOpenScheduleDialog(true)}
              >
                Schedule an Assessment
              </Button>
            </DisabledActionTooltip>
          </div>
        </div>
      )}

      <ScheduleDialog
        client={client}
        open={openScheduleDialog}
        startOnQuestionnaireTab={true}
        onClose={() => {
          setOpenScheduleDialog(false)
        }}
        canSchedule={canSchedule}
      />
    </>
  )
}

export default QuestionnairesOverview
